import { AdvancedSelect } from '../../../../widgets/select';
import { Dialog, DialogWindow, MessageDialog, useDialogManager } from '../../../../widgets/dialog';
import { FormControl } from '../../../../widgets/formControl';
import { Link } from '../../../../widgets/buttons';
import { SearchInput } from '../../../../widgets/input';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import { showNotification } from '../../../../widgets/notificationToaster';
import _ from 'lodash';
import React, { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { DialogOverlay } from 'src/components/pages/dashboard/intro/dialogDel';
import Loader from 'src/components/widgets/loader';
import { T, cartesianProduct, toLocalID, uuid } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import styled, { css } from 'styled-components';
import ConnectProductCatalogVariants from '../../connectProductCatalogVariants/connectProductCatalogVariants';
import { ModelColor, ProductColor } from '../../state/model/modelColor';
import { MerchantProductOption, ModelColorMapping } from '../../state/model/modelColorMapping';
import { merchantProduct_item$data } from '../../state/queries/__generated__/merchantProduct_item.graphql';
import SelectedOptionValue from '../../state/selectedOptionValue';
import StoreCatalog from '../../storeCatalog/storeCatalog';
import { ColorAttribute } from '../4-variants/helpers';
import { connectModelDialogModelColorsQuery } from './__generated__/connectModelDialogModelColorsQuery.graphql';
import { connectModelDialogQuery } from './__generated__/connectModelDialogQuery.graphql';
import { ModelColorMappingInput, connectModelDialogSetModelColorMappingMutation } from './__generated__/connectModelDialogSetModelColorMappingMutation.graphql';

const CssDialog = css`
    flex: 1;
    align-self: stretch;
    justify-self: stretch;
`;

const CustomDialogWindow = styled(DialogWindow)`
    ${CssDialog}
    max-width: 1320px;
`;

const CustomDialogWindowSmall = styled(DialogWindow)`
    ${CssDialog}
    max-width: 920px;
    max-height: 600px;
    place-self: center;
`;

const CustomDialogWindowNormal = styled(DialogWindow)`
    ${CssDialog}
    max-width: 1120px;
    max-height: 600px;
    place-self: center;
`;

const CustomDialogWindowLarge = styled(DialogWindow)`
    ${CssDialog}
    max-width: 1320px;
    max-height: 600px;
    place-self: center;
`;

export const ContainerCombo = styled(FormControl)`
    > label {
        display: flex;
        align-items: left;
        text-align: left;
        margin-bottom: 5px;
    }
    .comboControl {
        text-align: left;
    }
`;

const ConnectTextDialog = styled.div`
    width: 100%;
    margin: 0 auto;
    text-align: center;
`;

const RememberNotes = styled.div`
    box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.2), 0px 2px 10px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    padding: 8px 16px !important;
    background: #ffe1be;
    text-align: left;
    width: 100%;
    color: #a64f21;
    margin: -37px 0 30px !important;

    label {
        font-weight: 700;
        font-size: 14px;
        line-height: 20px;
        margin-bottom: 10px;
        display: block;
        &:last-child {
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
        }
    }
`;

interface HeaderDialogProps {
    isForImportingVariants?: boolean;
}

const HeaderDialog = styled.div<HeaderDialogProps>`
    label {
        display: block;
        margin-bottom: 5px;
        &:last-child {
            margin-bottom: 30px;
        }
        text-align: ${(props) => (props.isForImportingVariants ? 'center' : 'left')};
    }

    h5 {
        font-size: 24px;
        text-align: left;
        margin: 0px;
        margin-bottom: 8px;
    }
`;

const DialogOverlayImporting = styled(DialogOverlay)`
    z-index: 9999;
    justify-content: center;
`;

const TitleConnectProductCatalogVariants = styled.h2`
    text-align: center;
    margin: 20px 0 40px !important;
`;

const SubTitleConnectProductCatalogVariants = styled.h3`
    text-align: center;
    font-size: 15px;
    font-weight: normal;
    margin: -30px 0 40px !important;
`;

export const SearchContainer = styled.div<{ gridTemplateColumns?: string }>`
    display: grid;
    grid-gap: 30px;
    grid-template-columns: ${(props) => props.gridTemplateColumns || '1fr 621px'};
`;

const MerchantProductOptionsQuery: FC<{
    sku: string;
    salesChannelID: string;
    onOptionsFetchCompleted: (items: MerchantProductOption[]) => void;
}> = ({ sku, salesChannelID, onOptionsFetchCompleted }) => {
    const query = useLazyLoadQuery<connectModelDialogQuery>(
        graphql`
            query connectModelDialogQuery($sku: String!, $salesChannelID: ID!) {
                merchantProductOptions(sku: $sku, salesChannelID: $salesChannelID) {
                    salesChannelID
                    optionID
                    sku
                    label
                    isGlobal
                    values {
                        valueID
                        label
                    }
                }
            }
        `,
        { sku, salesChannelID }
    );

    useEffect(() => {
        const merchantOptionNoVariants = [{ salesChannelID: toLocalID(salesChannelID) ?? -1, optionID: '', sku, label: '', isGlobal: false, values: [] }];
        query &&
            onOptionsFetchCompleted(
                (query.merchantProductOptions.length > 0 ? query.merchantProductOptions : merchantOptionNoVariants) as MerchantProductOption[]
            );
    }, []);

    return null;
};

export interface PartialSalesChannel {
    id: string;
    detail: {
        id: string;
        properties: {
            name: string | null;
        } | null;
    } | null;
    ecommerceSettings: {
        definitiveUrl: string | null;
        ecommerceID: number;
        ecommerceName: string;
        hasProductsApi: boolean;
        isMarketplace: boolean;
        remoteUrl: string | null;
    } | null;
}

const ConnectModelDialog: FC<{
    productID: string;
    productName?: string;
    additionalCodes: { code: string; salesChannelID: string; merchantProductName: string }[];
    merchantProductOptionsConnected: MerchantProductOption[] | null | undefined;
    modelColorMappings?: ModelColorMapping[];
    productColors: ProductColor[] | ModelColor[];
    forImportingVariants?: boolean;
    forAddingNewSalesChannel?: boolean;
    forPublishingProduct?: boolean;
    forEditProduct?: boolean;
    forImportingTemplates?: boolean;
    selectedSKU?: string;
    selectedSalesChannelID?: string;
    salesChannels: PartialSalesChannel[];
    productSalesChannelID: string;
    windowDecorator?: React.FunctionComponent<any>;
    buttonSaveText?: string;
    queryKey?: string;

    mainStoreVariantsRemoved?: boolean;

    onCloseDialog?: () => void;
    onBackToSalesChannels?: () => void;
    onConfirmMappingVariants?: () => void;
    onConfirm?: (
        product: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'> | null,
        salesChannelId: string,
        productColorMappingToSave: ModelColorMapping[]
    ) => void;
    onConfirmImporting?: (productMerchantOptions: MerchantProductOption[]) => void;
}> = ({
    productID,
    productName,
    additionalCodes,
    merchantProductOptionsConnected,
    modelColorMappings,
    productColors,
    forImportingVariants,
    forAddingNewSalesChannel,
    forPublishingProduct,
    forEditProduct,
    forImportingTemplates,
    selectedSKU,
    selectedSalesChannelID,
    salesChannels,
    productSalesChannelID,
    windowDecorator,
    buttonSaveText,
    queryKey,
    mainStoreVariantsRemoved,
    onCloseDialog,
    onBackToSalesChannels,
    onConfirmMappingVariants,
    onConfirm,
    onConfirmImporting
}) => {
    // Queries

    let productColorsQuery: ProductColor[] | undefined = [];
    const { model } = useLazyLoadQuery<connectModelDialogModelColorsQuery>(
        graphql`
            query connectModelDialogModelColorsQuery($productID: ID!) {
                model(id: $productID) {
                    id
                    sourceProductTemplateID
                    name
                    colors {
                        colorID
                        code
                        name
                    }
                }
            }
        `,
        { productID: productID },
        { fetchPolicy: 'network-only', fetchKey: queryKey }
    );

    if (!productColors || productColors.length == 0) {
        productColorsQuery =
            model?.colors &&
            model.colors.map((c) => {
                return { code: c.code, colorCode: c.code, colorID: c.colorID, name: c.name } as ProductColor;
            });
    }

    // Mutations
    const setModelColorMappingMutation = graphql`
        mutation connectModelDialogSetModelColorMappingMutation($modelID: ID!, $modelColorMappings: [ModelColorMappingInput!]!) {
            setModelColorMapping(modelID: $modelID, modelColorMappings: $modelColorMappings) {
                colorID
                merchantCode
            }
        }
    `;

    // Data
    const productVariationsToConnect = (productColors.length == 0 ? productColorsQuery ?? [] : productColors) as ProductColor[];
    const numAdditionalCodes = additionalCodes.length == 0 ? 1 : forAddingNewSalesChannel ? additionalCodes.length + 1 : additionalCodes.length;

    // State
    const [selectedMerchantProduct, setSelectedMerchantProduct] = useState<Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'> | null>(null);
    const [selectedMerchantProductOptions, setSelectedMerchantProductOptions] = useState<MerchantProductOption[]>([]);
    const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>();
    const [salesChannelId, setSalesChannelId] = useState(salesChannels[0].id);
    const [dialogMode, setDialogMode] = useState<'Catalog' | 'MappingVariants'>(
        additionalCodes.length > 0 && !forAddingNewSalesChannel ? 'MappingVariants' : 'Catalog'
    );
    const [productColorMappingToSave, setProductColorMappingToSave] = useState<ModelColorMapping[]>([]);
    const [onQueryMerchantOptionInProgress, setOnQueryMerchantOptionInProgress] = useState(false);
    const [showApiWarning, setShowApiWarning] = useState(false);
    const [hasButtonPublishClicked, setHasButtonPublishClicked] = useState(false);
    const [search, setSearch] = useState('');

    const allMerchantProductOptionsConnected = (merchantProductOptionsConnected ?? [])
        .filter((mpo) => mpo.salesChannelID != toLocalID(salesChannelId) || forImportingTemplates || forEditProduct)
        .concat(selectedMerchantProductOptions);

    // CallBacks
    const handleProductClick = async (merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => {
        setOnQueryMerchantOptionInProgress(true);

        if (!merchantProduct.isConfiguratedInZakeke || forImportingVariants) {
            setSelectedMerchantProduct(merchantProduct);
        }
    };

    const handleSearch = (search: string) => {
        setSearch(search);
    };

    const handleConnectAndPublishClick = () => {
        setHasButtonPublishClicked(true);
        if ((onConfirm || onConfirmMappingVariants) && (selectedMerchantProduct || additionalCodes.length > 0) && productVariationsToConnect) {
            let selectedMerchantProductOptionsForSaving =
                selectedMerchantProductOptions.length > 0
                    ? selectedMerchantProductOptions
                    : (merchantProductOptionsConnected ?? [])
                          .filter((mpo) => mpo.salesChannelID != toLocalID(salesChannelId))
                          .concat(selectedMerchantProductOptions);

            let numStoreProductVariants = selectedMerchantProductOptionsForSaving.reduce(
                (accumulator, currentValue) => accumulator * currentValue.values.length,
                1
            );

            if (productColorMappingToSave.length > 0) {
                if (!forEditProduct && model?.sourceProductTemplateID != null) {
                    let merchantProductAttributeToMap = JSON.parse(productColorMappingToSave[0].productColor?.code || '[]') as ColorAttribute[];
                    numStoreProductVariants = 1;
                    merchantProductAttributeToMap.forEach((m) => {
                        let selectedMerchantProductAttribute = selectedMerchantProductOptionsForSaving.find((smpo) => smpo.optionID === m.Id);
                        numStoreProductVariants = numStoreProductVariants * (selectedMerchantProductAttribute?.values.length ?? 0);
                    });
                }

                if (productColorMappingToSave.length < numStoreProductVariants && model?.sourceProductTemplateID != null) {
                    showNotification({
                        type: 'danger',
                        message: (
                            <>
                                {T._(
                                    'Unable to map product variants. All product store attributes must be mapped with related product template attributes',
                                    'Admin'
                                )}
                            </>
                        ),
                        dismiss: {
                            duration: 3000
                        }
                    });
                    return;
                }

                let merchantVariantsToMap = productColorMappingToSave.map((x) => x.merchantCode);
                let productColorMappingSKU_SalesChannel = _.groupBy(productColorMappingToSave, (item) => item.salesChannelID);
                let numSalesChannelConnected =
                    additionalCodes.length == 0
                        ? 0
                        : selectedSalesChannelID && additionalCodes.some((ac) => toLocalID(ac.salesChannelID) == toLocalID(selectedSalesChannelID))
                        ? additionalCodes.length
                        : selectedSalesChannelID == undefined || toLocalID(selectedSalesChannelID) == toLocalID(productSalesChannelID)
                        ? additionalCodes.length
                        : additionalCodes.length + 1;

                if (Object.entries(productColorMappingSKU_SalesChannel).length < numSalesChannelConnected && forEditProduct) {
                    showNotification({
                        type: 'danger',
                        message: (
                            <>
                                {T._(
                                    'Unable to map product variants. Every attributes must be mapped with different merchant product attributes and/or all options must be mapped',
                                    'Admin'
                                )}
                            </>
                        ),
                        dismiss: {
                            duration: 3000
                        }
                    });
                    return;
                }

                if (forImportingTemplates) {
                    for (let indxSalesChannel = 0; indxSalesChannel < numSalesChannelConnected; indxSalesChannel++) {
                        if (
                            new Set(Object.entries(productColorMappingSKU_SalesChannel)[indxSalesChannel][1].map((m) => m.merchantCode)).size !=
                                Object.entries(productColorMappingSKU_SalesChannel)[indxSalesChannel][1].map((m) => m.merchantCode).length &&
                            model?.sourceProductTemplateID != null
                        ) {
                            showNotification({
                                type: 'danger',
                                message: (
                                    <>
                                        {T._(
                                            "It was not possible to save because you've assigned the same template's attribute to multiple product's attributes. Please review your attribute assignments and make sure there is a 1:1 mapping before saving again.",
                                            'Admin'
                                        )}
                                    </>
                                ),
                                dismiss: {
                                    duration: 3000
                                }
                            });
                            return;
                        }
                    }
                }

                let isProductVariantsWithoutMappings = false;
                // Check if for product with variants has all mapping with saleschannel products without variants
                _.forEach(productColorMappingSKU_SalesChannel, (group, key) => {
                    if (group.length == 1 && group[0].colorID == -1 && group[0].merchantCode == '[]' && !forEditProduct)
                        isProductVariantsWithoutMappings = true;
                });

                if (isProductVariantsWithoutMappings) {
                    showNotification({
                        type: 'danger',
                        message: (
                            <>
                                {T._(
                                    'Unable to map product variants. Every attributes must be mapped with different merchant product attributes and/or all options must be mapped',
                                    'Admin'
                                )}
                            </>
                        ),
                        dismiss: {
                            duration: 3000
                        }
                    });
                    return;
                }

                let mappingsToSave = [...productColorMappingToSave];
                if (model?.sourceProductTemplateID != null && mappingsToSave[0].merchantCode != '[]' && !forEditProduct) {
                    mappingsToSave = convertMappingToSaveForTemplate(productColorMappingToSave);
                }

                if (mappingsToSave.length > 0 && !mappingsToSave.some((p) => p.colorID == -1)) {
                    updateModelColorMappings({
                        variables: {
                            modelID: productID,
                            modelColorMappings: mappingsToSave.map((m) => {
                                return {
                                    colorID: m.colorID,
                                    salesChannelID: m.salesChannelID,
                                    merchantCode: m.merchantCode
                                } as ModelColorMappingInput;
                            })
                        },
                        onError: (error) => {
                            showNotification({
                                type: 'danger',
                                message: <>{T._('An error occurred while mapping product variants. Please try again.', 'Admin')}</>,
                                dismiss: {
                                    duration: 3000
                                }
                            });
                        },
                        onCompleted: (response) => {
                            if (additionalCodes.length > 0 && onConfirmMappingVariants) onConfirmMappingVariants();
                            else if (onConfirm) onConfirm(selectedMerchantProduct, salesChannelId, mappingsToSave);
                        }
                    });
                } else {
                    if (onConfirm) onConfirm(selectedMerchantProduct, salesChannelId, mappingsToSave);
                }
            } else if (productColorMappingToSave.length == 0 && productVariationsToConnect[0].colorID != 1) {
                showNotification({
                    type: 'danger',
                    message: (
                        <>
                            {T._(
                                'Unable to map product variants. Every attributes must be mapped with different merchant product attributes and/or all options must be mapped',
                                'Admin'
                            )}
                        </>
                    ),
                    dismiss: {
                        duration: 3000
                    }
                });
            } else onConfirm && onConfirm(selectedMerchantProduct, salesChannelId, productColorMappingToSave);
        }
    };

    // Utility functions
    const convertProductVariantMerchantOptions = (variants: ProductColor[], salesChannelID: string): MerchantProductOption[] => {
        let merchantProductOptionsFromVariants: MerchantProductOption[] = [];
        let attributes = [...variants].map((modelcolor) => JSON.parse(modelcolor.code || '[]') as ColorAttribute[]);
        for (let i = 0; i < attributes.length; i++) {
            attributes[i].forEach((attr, j) => {
                let merchantProductOptionsFromVariantsAttribute = merchantProductOptionsFromVariants.find((mpov) => mpov.optionID === attr.Id);
                if (merchantProductOptionsFromVariantsAttribute) {
                    if (!merchantProductOptionsFromVariantsAttribute.values.some((o) => o.valueID === attr.Value.Id)) {
                        merchantProductOptionsFromVariantsAttribute.values.push({
                            label: attr.Value.Label,
                            valueID: attr.Value.Id
                        });
                    }
                } else {
                    merchantProductOptionsFromVariants.push({
                        isGlobal: true,
                        optionID: attr.Id,
                        label: attr.Label,
                        sku: '',
                        salesChannelID: toLocalID(salesChannelID) ?? -1,
                        values: [
                            {
                                label: attr.Value.Label,
                                valueID: attr.Value.Id
                            }
                        ]
                    });
                }
            });
        }
        return merchantProductOptionsFromVariants;
    };

    const convertMerchantOptionsToProductVariants = (merchantProductOptions: MerchantProductOption[]): ProductColor[] => {
        let productColorFromMerchantProductOptions: ProductColor[] = [];
        let variantsImported = merchantProductOptions.map((attr) =>
            attr.values.map((opt) => {
                return { Id: attr.optionID, Label: attr.label, Value: { Id: opt.valueID, Label: opt.label } };
            })
        );
        let variantsToSave = merchantProductOptions.length == 1 ? variantsImported[0].map((v) => [v]) : cartesianProduct(...variantsImported);

        variantsToSave.forEach((variant: SelectedOptionValue[]) => {
            const name = variant.map((x) => x.Value.Label).join(' - ');
            const code = JSON.stringify(variant);
            let color: ProductColor = {
                code,
                colorID: -1,
                guid: uuid(),
                name,
                colorCode: code
            };
            productColorFromMerchantProductOptions.push(color);
        });
        return productColorFromMerchantProductOptions;
    };

    const convertMappingToSaveForTemplate = (sourceModelColorMappings: ModelColorMapping[]): ModelColorMapping[] => {
        let outputColorMappingsToSave: ModelColorMapping[] = [];
        sourceModelColorMappings.forEach((sourceMapping) => {
            let newModelColorMapping: ModelColorMapping = {} as ModelColorMapping;
            newModelColorMapping.merchantCode = sourceMapping.productColor?.code ?? '[]';

            let productColorToMap = productVariationsToConnect.find((pc) => pc.code === sourceMapping.merchantCode);
            newModelColorMapping.colorID = productColorToMap?.colorID ?? -1;
            newModelColorMapping.salesChannelID = sourceMapping.salesChannelID;
            outputColorMappingsToSave.push(newModelColorMapping);
        });
        return outputColorMappingsToSave;
    };

    // Hooks
    const options = useMemo(() => {
        return (
            salesChannels?.map((salesChannel) => ({
                value: salesChannel!.id,
                label: salesChannel.detail?.properties?.name ?? salesChannel.ecommerceSettings?.remoteUrl
            })) ?? []
        );
    }, [salesChannels]);

    const [updateModelColorMappings, isUpdateMappingInProgress] = useMutation<connectModelDialogSetModelColorMappingMutation>(setModelColorMappingMutation);
    const { showDialog, closeDialog } = useDialogManager();

    useEffect(() => {
        const channel = salesChannels.find((s) => s.id === salesChannelId);
        setShowApiWarning(channel?.ecommerceSettings?.ecommerceName === 'API' && !channel?.ecommerceSettings?.hasProductsApi);
    }, [salesChannelId]);

    useEffect(() => {
        if (productVariationsToConnect.length == 1 && productVariationsToConnect[0].colorID == 1) {
            handleConnectAndPublishClick();
        } else setDialogMode('MappingVariants');
    }, [selectedMerchantProductOptions]);

    useEffect(() => {
        setHasButtonPublishClicked(false);
        if (selectedMerchantProduct == null && !forEditProduct && !forImportingTemplates) {
            setDialogMode('Catalog');
        }
    }, [selectedMerchantProduct]);

    // Render
    return salesChannels.length >= 1 ? (
        <Dialog
            titleHtml={
                dialogMode === 'MappingVariants' && (
                    <React.Fragment>
                        <TitleConnectProductCatalogVariants>
                            {!forEditProduct ? T._('Mapping product variants', 'Admin') : T._('Edit mapping sales channels', 'Admin')}
                        </TitleConnectProductCatalogVariants>
                        {!forEditProduct &&
                            model?.sourceProductTemplateID != null &&
                            selectedMerchantProductOptions.length > 0 &&
                            selectedMerchantProductOptions[0].values.length > 0 && (
                                <SubTitleConnectProductCatalogVariants>
                                    {T._("Assign attributes from a template to the product's attributes from the store", 'Admin')}
                                </SubTitleConnectProductCatalogVariants>
                            )}
                        {!forEditProduct &&
                            model?.sourceProductTemplateID != null &&
                            selectedMerchantProductOptions.length > 0 &&
                            selectedMerchantProductOptions[0].values.length == 0 && (
                                <SubTitleConnectProductCatalogVariants>
                                    {T._('Assign template variant to the product store', 'Admin')}
                                </SubTitleConnectProductCatalogVariants>
                            )}
                    </React.Fragment>
                )
            }
            windowDecorator={
                dialogMode === 'MappingVariants'
                    ? numAdditionalCodes == 1
                        ? CustomDialogWindowSmall
                        : numAdditionalCodes <= 2
                        ? CustomDialogWindowNormal
                        : CustomDialogWindowLarge
                    : CustomDialogWindow
            }
            onClose={onCloseDialog}
            buttons={
                forImportingVariants
                    ? []
                    : dialogMode === 'MappingVariants'
                    ? !forEditProduct && forImportingTemplates !== true
                        ? [
                              {
                                  label: T._('Back to catalog', 'Admin'),
                                  onClick: () => {
                                      setSelectedMerchantProduct(null);
                                      setDialogMode('Catalog');
                                  },
                                  linkButton: true
                              },
                              { label: buttonSaveText ? buttonSaveText : T._('Publish', 'Admin'), onClick: handleConnectAndPublishClick }
                          ]
                        : [{ label: buttonSaveText ? buttonSaveText : T._('Publish', 'Admin'), onClick: handleConnectAndPublishClick }]
                    : onBackToSalesChannels
                    ? [{ label: T._('Back to sales channels', 'Admin'), onClick: onBackToSalesChannels }]
                    : []
            }
            ref={(instance) => setScrollContainer(instance)}
        >
            {dialogMode === 'Catalog' && showApiWarning && forAddingNewSalesChannel && (
                <ConnectTextDialog>
                    <HeaderDialog>
                        <h5>{T._('Add product to a Sales Channel', 'Admin')}</h5>
                        <label>{T._('Choose a sales channel and a product from your store to connect with this Zakeke product.', 'Admin')}</label>
                    </HeaderDialog>
                    {salesChannels.length > 1 && !selectedSalesChannelID ? (
                        <ContainerCombo>
                            <SearchContainer>
                                <AdvancedSelect
                                    options={options}
                                    className='comboControl'
                                    value={options.find((s) => s.value === salesChannelId)}
                                    placeholder={T._('Select sales channel', 'Admin')}
                                    onChange={(item) => {
                                        setSalesChannelId(item?.value!);
                                    }}
                                    isDisabled={salesChannels.length == 1}
                                />
                            </SearchContainer>
                        </ContainerCombo>
                    ) : null}
                    <div style={{ padding: '16px 0' }}>
                        <NotificationBanner
                            type='WARNING'
                            content={
                                <>
                                    {T._('You must implement the', 'Admin')}{' '}
                                    <Link href='https://docs.zakeke.com/docs/API/Integration/Connecting-Product/Products_Catalog_API' target='_blank'>
                                        {T._('Product Catalog API', 'Admin')}
                                    </Link>{' '}
                                    {T._('in order to connect and publish the product.', 'Admin')}
                                </>
                            }
                        />
                    </div>
                </ConnectTextDialog>
            )}
            {dialogMode === 'Catalog' && !showApiWarning && (
                <StoreCatalog
                    salesChannelID={salesChannelId}
                    onProductClick={handleProductClick}
                    scrollContainer={scrollContainer}
                    cardTitleHtml={
                        <ConnectTextDialog>
                            {forImportingVariants ? (
                                <HeaderDialog isForImportingVariants={true}>
                                    <label>{T._('Choose product where you want to import variants', 'Admin')}</label>
                                </HeaderDialog>
                            ) : (
                                <>
                                    {forAddingNewSalesChannel ? (
                                        <HeaderDialog>
                                            <h5>{T._('Add product to a Sales Channel', 'Admin')}</h5>
                                            <label>
                                                {T._('Choose a sales channel and a product from your store to connect with this Zakeke product.', 'Admin')}
                                            </label>
                                        </HeaderDialog>
                                    ) : (
                                        <HeaderDialog>
                                            <h5>{T._('Select a store product', 'Admin')}</h5>
                                            <label>
                                                {T._(
                                                    'Choose a product from your store to associate with this Zakeke product. Once linked, the store product becomes customizable via Zakeke.',
                                                    'Admin'
                                                )}
                                            </label>
                                        </HeaderDialog>
                                    )}
                                </>
                            )}
                            {salesChannels.length > 1 && !selectedSalesChannelID ? (
                                <ContainerCombo>
                                    <SearchContainer>
                                        <AdvancedSelect
                                            options={options}
                                            className='comboControl'
                                            value={options.find((s) => s.value === salesChannelId)}
                                            placeholder={T._('Select sales channel', 'Admin')}
                                            onChange={(item) => {
                                                setSalesChannelId(item?.value!);
                                            }}
                                            isDisabled={salesChannels.length == 1}
                                        />
                                        <SearchInput fullWidth placeholder={T._('Search by product name or SKU...', 'Admin')} onSearch={handleSearch} />
                                    </SearchContainer>
                                </ContainerCombo>
                            ) : (
                                <ContainerCombo>
                                    <SearchInput fullWidth placeholder={T._('Search by product name or SKU...', 'Admin')} onSearch={handleSearch} />
                                </ContainerCombo>
                            )}
                        </ConnectTextDialog>
                    }
                    selectedProductsSKUs={selectedMerchantProduct && !forImportingVariants ? [selectedMerchantProduct.sku] : []}
                    showHeader={false}
                    searchProduct={search}
                    isAddSalesChannel={forAddingNewSalesChannel}
                    isPublishingProduct={forPublishingProduct}
                    isForImportingVariants={forImportingVariants}
                />
            )}
            {dialogMode === 'MappingVariants' && !onQueryMerchantOptionInProgress && (
                <Suspense
                    fallback={
                        <DialogOverlayImporting>
                            <Loader />
                        </DialogOverlayImporting>
                    }
                >
                    <ConnectProductCatalogVariants
                        forEditProduct={forEditProduct ?? false}
                        hasButtonPublishClicked={hasButtonPublishClicked}
                        isProductTemplate={model?.sourceProductTemplateID != null}
                        productZakekeName={productName ?? model?.name ?? ''}
                        productSalesChannelUrl={salesChannels.find((sc) => sc.id == productSalesChannelID)?.ecommerceSettings?.remoteUrl ?? null}
                        additionalCodes={additionalCodes}
                        selectedMerchantProductName={selectedMerchantProduct?.name ?? ''}
                        productColorsToConnect={
                            !forEditProduct &&
                            model?.sourceProductTemplateID != null &&
                            allMerchantProductOptionsConnected.length > 0 &&
                            allMerchantProductOptionsConnected[0].values.length > 0
                                ? convertMerchantOptionsToProductVariants(
                                      (merchantProductOptionsConnected ?? [])
                                          .filter((mpo) => mpo.salesChannelID != toLocalID(salesChannelId) || forImportingTemplates)
                                          .concat(selectedMerchantProductOptions)
                                  )
                                : productVariationsToConnect
                        }
                        merchantProductOptionsLoaded={
                            !forEditProduct &&
                            model?.sourceProductTemplateID != null &&
                            allMerchantProductOptionsConnected.length > 0 &&
                            allMerchantProductOptionsConnected[0].values.length > 0
                                ? convertProductVariantMerchantOptions(
                                      productVariationsToConnect,
                                      forImportingTemplates === true
                                          ? additionalCodes.length > 0
                                              ? additionalCodes[0].salesChannelID
                                              : salesChannelId
                                          : salesChannelId
                                  )
                                : allMerchantProductOptionsConnected.length == 0
                                ? merchantProductOptionsConnected
                                : allMerchantProductOptionsConnected
                        }
                        salesChannelID={salesChannelId}
                        salesChannels={salesChannels}
                        mappingsSet={modelColorMappings}
                        merchantProductSku={
                            additionalCodes.length > 0 && !forAddingNewSalesChannel ? additionalCodes[0].code : selectedMerchantProduct?.sku ?? ''
                        }
                        onConfirmImporting={onConfirmImporting}
                        onErrorImporting={() => {
                            setSelectedMerchantProduct(null);
                        }}
                        onCompleteLoadingMerchantAttributes={() => {
                            setDialogMode('MappingVariants');
                        }}
                        onUpdateMapping={(mappingsToSave) => {
                            setProductColorMappingToSave(mappingsToSave);
                        }}
                        enableMultiSalesChannels={forAddingNewSalesChannel === true}
                        onlyImporting={forImportingVariants === true}
                        mainStoreVariantsRemoved={mainStoreVariantsRemoved}
                    />
                </Suspense>
            )}

            {isUpdateMappingInProgress && (
                <DialogOverlayImporting>
                    <Loader />
                </DialogOverlayImporting>
            )}

            {selectedSKU && selectedSalesChannelID && (
                <Suspense
                    fallback={
                        <DialogOverlayImporting>
                            <Loader />
                        </DialogOverlayImporting>
                    }
                >
                    <MerchantProductOptionsQuery
                        sku={selectedSKU}
                        salesChannelID={selectedSalesChannelID}
                        onOptionsFetchCompleted={(items) => {
                            setSelectedMerchantProductOptions(items);
                            setOnQueryMerchantOptionInProgress(false);
                            if (!(productVariationsToConnect.length == 1 && productVariationsToConnect[0].colorID == 1)) setDialogMode('MappingVariants');
                        }}
                    ></MerchantProductOptionsQuery>
                </Suspense>
            )}

            {selectedMerchantProduct && onQueryMerchantOptionInProgress && (
                <Suspense
                    fallback={
                        <DialogOverlayImporting>
                            <Loader />
                        </DialogOverlayImporting>
                    }
                >
                    <MerchantProductOptionsQuery
                        sku={selectedMerchantProduct.sku}
                        salesChannelID={salesChannelId}
                        onOptionsFetchCompleted={(items) => {
                            setSelectedMerchantProductOptions(items);
                            setOnQueryMerchantOptionInProgress(false);
                        }}
                    ></MerchantProductOptionsQuery>
                </Suspense>
            )}
        </Dialog>
    ) : null;
};

export default ConnectModelDialog;
